import {
  PaletteIcon,
  PuzzleIcon,
  FlaskConicalIcon,
  ChefHatIcon,
  HammerIcon,
  TreePineIcon,
  type LucideIcon,
} from 'lucide-react';

export type Tome = {
  n: number;
  title: string;
  content: string;
  icon: LucideIcon;
  color: string;
};

export const tomes: Tome[] = [
  { n: 1, title: '30 Días de Creatividad Sin Pantallas', content: 'Dibujo, arte, manualidades y proyectos creativos', icon: PaletteIcon, color: '#ff6b57' },
  { n: 2, title: 'Misiones y Juegos Sin Pantallas', content: 'Aventuras, juegos familiares, imaginación y desafíos', icon: PuzzleIcon, color: '#4b8df8' },
  { n: 3, title: 'Pequeños Científicos', content: 'Experimentos sencillos, observación y descubrimientos', icon: FlaskConicalIcon, color: '#2bd46b' },
  { n: 4, title: 'Pequeños Chefs', content: 'Recetas sin fuego, creatividad culinaria y preparaciones sencillas', icon: ChefHatIcon, color: '#ffc83d' },
  { n: 5, title: 'Ingenieros en Acción', content: 'Construcciones, inventos y proyectos con papel y cartón', icon: HammerIcon, color: '#a07bf7' },
  { n: 6, title: 'Aventuras al Aire Libre', content: 'Exploración, naturaleza, observación y actividades familiares', icon: TreePineIcon, color: '#22c3a6' },
];

export const tomeIncludes = [
  '20 actividades completas con instrucciones paso a paso.',
  '10 desafíos rápidos para variar las propuestas.',
  '10 recursos imprimibles para complementar las actividades.',
  'Listas de materiales e indicaciones de preparación.',
  'Actividades organizadas por temática.',
  'Propuestas para niños de 5 a 9 años.',
  'Recursos que puedes consultar y volver a utilizar.',
  'Actividades que favorecen la creatividad, la curiosidad y los momentos compartidos.',
];

export const bonuses = [
  { n: '01', title: '90 Días de Diversión Sin Pantallas', solves: 'Organiza 90 actividades en calendarios y planificadores listos para usar' },
  { n: '02', title: 'Kit SOS Antipantallas', solves: '32 actividades rápidas para cuando necesitas una solución inmediata' },
  { n: '03', title: 'La Caja de Aventuras Sorpresa', solves: '36 tarjetas y 12 giros creativos para combinar actividades' },
  { n: '04', title: 'Aventuras en Ruta', solves: '24 juegos para viajes, esperas y salidas familiares' },
  { n: '05', title: 'Kit de Rutinas Creativas para la Familia', solves: '12 rutinas prácticas para incorporar actividades a la vida cotidiana' },
];

export const pains = [
  'Tu hijo vuelve a pedir el celular.',
  'Pasas horas buscando ideas en internet.',
  'Las actividades piden materiales complicados.',
  'Siempre terminan en los mismos juegos.',
  'Preparar una actividad se vuelve otra tarea.',
  'Cada fin de semana: buscar, organizar, improvisar.',
];

export const shortReviews = [
  { quote: '[Insertar aquí una opinión auténtica sobre la facilidad de encontrar actividades preparadas.]', name: '[Nombre autorizado]', country: '[País de LATAM]' },
  { quote: '[Insertar aquí una opinión auténtica sobre los recursos imprimibles y los juegos en familia.]', name: '[Nombre autorizado]', country: '[País de LATAM]' },
  { quote: '[Insertar aquí una opinión auténtica sobre la variedad de actividades de los seis tomos.]', name: '[Nombre autorizado]', country: '[País de LATAM]' },
];

export const longTestimonials = [
  { quote: '[Testimonio real de una familia que haya utilizado el planificador de 90 días y explique su experiencia concreta.]', name: '[Nombre autorizado]', country: '[País de LATAM]', product: 'MegaPack Antipantallas Listo' },
  { quote: '[Testimonio real relacionado con los juegos, misiones o actividades creativas.]', name: '[Nombre autorizado]', country: '[País de LATAM]', product: null },
  { quote: '[Testimonio real relacionado con los imprimibles o con una actividad de alguno de los tomos.]', name: '[Nombre autorizado]', country: '[País de LATAM]', product: null },
  { quote: '[Testimonio real relacionado con la facilidad de utilización y la variedad del MegaPack.]', name: '[Nombre autorizado]', country: '[País de LATAM]', product: null },
];

// Compras recientes para las notificaciones. Reemplazar con datos reales de la tienda.
export const recentPurchases = [
  { name: '[Nombre]', country: '[País]', when: 'hace unos minutos' },
  { name: '[Nombre]', country: '[País]', when: 'hace unos minutos' },
  { name: '[Nombre]', country: '[País]', when: 'hace unos minutos' },
  { name: '[Nombre]', country: '[País]', when: 'hace unos minutos' },
];

export const upsellRows = [1, 2, 3, 4, 5].map((i) => ({
  situation: `[Situación específica ${i}]`,
  without: '[Dificultad real]',
  with: '[Herramienta incluida]',
}));

export const faqs = [
  { q: '¿Sirve para niños de diferentes edades?', a: ['Sí. El MegaPack está pensado principalmente para niños de 5 a 9 años. Las actividades están organizadas en seis temáticas y algunas pueden adaptarse según la edad, los intereses y las habilidades de cada niño.'] },
  { q: '¿Cómo recibo el material?', a: ['Después de completar y confirmarse tu pago, recibirás acceso a los archivos digitales mediante el sistema de entrega de ReadyKit Digital.', 'Los tomos y bonos se entregan en formato PDF.'] },
  { q: '¿Qué lo diferencia de las actividades gratuitas de internet?', a: ['El MegaPack reúne seis colecciones de actividades guiadas y cinco bonos complementarios en un sistema organizado.', 'No son ideas dispersas: encontrarás instrucciones, materiales, desafíos, recursos imprimibles y herramientas para elegir propuestas según tus necesidades.'] },
  { q: '¿Necesito imprimir todo?', a: ['No. Puedes consultar las instrucciones desde un dispositivo e imprimir solamente las fichas, tarjetas, calendarios y recursos que quieras utilizar.'] },
  { q: '¿Necesito materiales especiales?', a: ['Muchas actividades utilizan papel, lápices, cartón y materiales cotidianos. Algunas requieren ingredientes o elementos específicos, que se indican en sus instrucciones.'] },
  { q: '¿Puedo comprar desde cualquier país de LATAM?', a: ['El MegaPack está dirigido a familias de Latinoamérica. Los países y medios de pago admitidos se muestran en la plataforma de cobro.'] },
  { q: '¿Puedo pagar en mi moneda local?', a: ['Sí, en los países donde esté habilitado el cobro en moneda local. Durante el proceso de pago podrás consultar el importe final en la moneda disponible para tu país.'] },
  { q: '¿Es una suscripción mensual?', a: ['No. Es una compra única de productos digitales, sin membresía mensual.'] },
  { q: '¿Tiene garantía?', a: ['Consulta las condiciones de garantía y devolución publicadas por ReadyKit Digital. Si está habilitada la garantía de siete días, podrás solicitarla dentro del plazo y las condiciones indicadas.'] },
  { q: '¿Cómo contacto al soporte?', a: ['Escríbenos a:'], email: true },
];

export const costRows = [
  { without: 'Buscar actividades en internet cada vez que aparece el aburrimiento', cost: 'Tiempo dedicado a encontrar y organizar ideas' },
  { without: 'Encontrar manualidades sin instrucciones claras', cost: 'Preparaciones que no siempre salen como esperabas' },
  { without: 'No tener recursos imprimibles preparados', cost: 'Volver a buscar o crear materiales desde cero' },
  { without: 'Querer hacer actividades diferentes y no saber cuáles elegir', cost: 'Terminar repitiendo las mismas propuestas' },
  { without: 'No tener tiempo para planificar', cost: 'Dejar las actividades para otro día' },
  { without: 'Escuchar «Estoy aburrido» y no tener ninguna alternativa a mano', cost: 'Volver al mismo ciclo de improvisación' },
];

export const closingLines = [
  'Una actividad de dibujo.',
  'Una misión familiar.',
  'Un experimento sorprendente.',
  'Una receta sencilla.',
  'Un proyecto de construcción.',
  'Una aventura en la naturaleza.',
];
